// src/components/BannerCarousel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";

export default function BannerCarousel({
  placement = "HOME_TOP",
  type = "SLIDER",
  fullBleed = true,
  autoPlay = true,
  intervalMs = 3000,        // ✅ 3 seconds
  showCaption = false,
  maxWidth = "1400px",         // ✅ Centered with max width for better desktop alignment
  sideGap = 0,               // ✅ No side gap by default
  arrowsOutside = false,     // ✅ Keep arrows inside for clean edges
  heightDesktop = 600,       // ✅ Even taller for more impact
  heightTablet = 450,
  heightMobile = 320,
}) {

  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [itemsPerRow, setItemsPerRow] = useState(1);

  const timer = useRef(null);
  const paused = useRef(false);
  const touch = useRef({ startX: 0, endX: 0 });

  useEffect(() => {
    const update = () => {
      setItemsPerRow(1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const imgUrl = (path) => {
    if (!path) return "";
    let base = (axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
    base = base.replace(/\/api$/i, ""); // Ensure we don't have /api in image paths
    if (String(path).startsWith("http")) return path;
    if (String(path).startsWith("/")) return `${base}${path}`;
    return `${base}/${path}`;
  };

  const total = items.length;
  const current = useMemo(() => items[index], [items, index]);

  // --- load banners ---
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axiosInstance.get("/api/banners/public", {
          params: { placement, type },
        });

        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.banners)
            ? data.banners
            : [];

        if (!mounted) return;

        const activeList = list.filter((b) => b?.isActive !== false);
        setItems(activeList);
        setIndex(0);
      } catch (e) {
        console.log("banner load error", e);
        if (!mounted) return;
        setItems([]);
        setErr("Failed to load banners");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [placement, type]);

  // --- autoplay ---
  useEffect(() => {
    if (!autoPlay) return;
    const maxIdx = Math.max(0, total - itemsPerRow);
    if (maxIdx <= 0) return;

    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (!paused.current) {
        setIndex((i) => (i >= maxIdx ? 0 : i + 1));
      }
    }, intervalMs);

    return () => { if (timer.current) clearInterval(timer.current); };
  }, [total, autoPlay, intervalMs, itemsPerRow]);

  // --- helpers ---
  const maxIdx = Math.max(0, total - itemsPerRow);
  const prev = () => setIndex((i) => (i <= 0 ? maxIdx : i - 1));
  const next = () => setIndex((i) => (i >= maxIdx ? 0 : i + 1));



  // --- swipe ---
  const onTouchStart = (e) => {
    paused.current = true;
    touch.current.startX = e.touches[0].clientX;
    touch.current.endX = e.touches[0].clientX;
  };
  const onTouchMove = (e) => { touch.current.endX = e.touches[0].clientX; };
  const onTouchEnd = () => {
    if (total <= itemsPerRow) return;
    const dx = touch.current.endX - touch.current.startX;
    paused.current = false;
    if (Math.abs(dx) < 45) return;
    if (dx > 0) prev(); else next();
  };

  // --- keyboard ---
  const onKeyDown = (e) => {
    if (total <= 1) return;
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  // ✅ NO top padding/margin, only 1px left/right
  const wrapStyle = {
    paddingLeft: sideGap,
    paddingRight: sideGap,
    paddingTop: 0,
    marginTop: 0,
  };

  const innerStyle = fullBleed ? { maxWidth: "none" } : { maxWidth: maxWidth };

  // --- loading state ---
  if (loading) {
    return (
      <div
        className={`bc-wrap ${fullBleed ? "bc-fullbleed" : ""}`}
        style={{ ...wrapStyle, "--items-per-row": itemsPerRow }}
      >
        <style>{css(heightDesktop, heightTablet, heightMobile, arrowsOutside ? "outside" : "inside")}</style>
        <div className="bc-center">
          <div className="bc-inner" style={innerStyle}>
            <div className="bc-shell bc-skeleton" />
          </div>
        </div>
      </div>
    );
  }

  if (err || total === 0) return null;

  return (
    <div
      className={`bc-wrap ${fullBleed ? "bc-fullbleed" : ""}`}
      style={{ ...wrapStyle, "--items-per-row": itemsPerRow }}
    >
      <style>{css(heightDesktop, heightTablet, heightMobile, arrowsOutside ? "outside" : "inside")}</style>

      <div className="bc-center">
        <div className="bc-inner" style={innerStyle}>
          <div
            className={`bc-shell ${type === "CAROUSEL" ? "bc-carousel" : ""}`}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onMouseEnter={() => (paused.current = true)}
            onMouseLeave={() => (paused.current = false)}
          >
            <div
              className="bc-track"
              style={{ transform: `translateX(-${index * (100 / itemsPerRow)}%)` }}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {items.map((b) => {
                const link = b?.linkUrl || "";
                const isExternal = link.startsWith("http");

                return (
                  <div
                    key={b.id}
                    className="bc-slide"
                  >
                    <img
                      src={imgUrl(b.image)}
                      alt={b.title || "banner"}
                      className="bc-img"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />

                    {showCaption && (b.title || b.subtitle) && (
                      <div className="bc-caption">
                        {b.title && <div className="bc-title">{b.title}</div>}
                        {b.subtitle && <div className="bc-sub">{b.subtitle}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {total > 1 && (
              <>
                <button type="button" className="bc-nav bc-left" onClick={prev} aria-label="Prev">‹</button>
                <button type="button" className="bc-nav bc-right" onClick={next} aria-label="Next">›</button>
              </>
            )}
          </div>

          {total > itemsPerRow && (
            <div className="bc-dotsRow" aria-label="Carousel dots">
              {items.slice(0, total - itemsPerRow + 1).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`bc-dot ${i === index ? "active" : ""}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ✅ CSS: no top gap, 1px sides, 2s smooth slide */
function css(hD, hT, hM, arrowMode) {
  return `
    *, *::before, *::after { box-sizing: border-box; }

    /* ✅ No top gap at all */
    .bc-wrap {
      width: 100%;
      margin: 0;
      padding-top: 0 !important;
      margin-top: 0 !important;
      background: #fff;
      padding-left: 0;
      padding-right: 0;
    }
    @media (max-width: 640px) {
      .bc-wrap {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }

    .bc-fullbleed {
      width: 100vw;
      margin-left: calc(50% - 50vw);
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .bc-center {
      width: 100%;
      display: flex;
      justify-content: center;
    }

    .bc-inner {
      width: 100%;
      transition: max-width 0.3s ease;
    }

    .bc-shell {
      position: relative;
      width: 100%;
      overflow: hidden;
      background: transparent;
      outline: none;
    }
    .bc-carousel { border-radius: 0; box-shadow: 0 20px 40px rgba(0,0,0,0.1); }

    /* ✅ smooth 500ms slide transition */
    .bc-track {
      display: flex;
      transition: transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
      will-change: transform;
    }

    .bc-slide {
      min-width: calc(100% / var(--items-per-row, 1));
      position: relative;
      display: block;
      text-decoration: none;
      color: inherit;
      padding: 0; // ✅ No gap needed for single row
    }

    .bc-img {
      width: 100%;
      height: clamp(320px, 40vw, ${hD}px);
      object-fit: fill; // ✅ Stretch to fill as requested
      object-position: center;
      display: block;
      background: #ffffff;
      border-radius: 0;
      box-shadow: none;
    }
    @media (max-width: 1024px) {
      .bc-img { height: ${hT}px; }
    }
    @media (max-width: 640px) {
      .bc-wrap {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
      .bc-img { height: ${hM}px; border-radius: 0; }
      .bc-shell { border-radius: 0; border: none; }
    }

    /* caption */
    .bc-caption {
      position: absolute;
      left: 14px;
      right: 14px;
      bottom: 14px;
      padding: 12px;
      border-radius: 14px;
      background: linear-gradient(180deg, rgba(0,0,0,.05), rgba(0,0,0,.55));
      backdrop-filter: blur(6px);
      color: #fff;
      max-width: 680px;
    }
    .bc-title {
      font-size: 18px;
      font-weight: 900;
      letter-spacing: .2px;
      text-shadow: 0 2px 10px rgba(0,0,0,.25);
    }
    .bc-sub {
      margin-top: 4px;
      font-size: 13px;
      opacity: .95;
    }

    /* arrows - white circles */
    .bc-nav {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 46px;
      height: 46px;
      border-radius: 999px;
      border: 1px solid rgba(15,23,42,.14);
      background: #ffffff;
      color: #0f172a;
      font-size: 28px;
      cursor: pointer;
      display: grid;
      place-items: center;
      box-shadow: 0 10px 18px rgba(0,0,0,.12);
      user-select: none;
      z-index: 10;
      transition: transform 180ms ease, box-shadow 180ms ease;
    }
    .bc-nav:hover {
      box-shadow: 0 14px 24px rgba(0,0,0,.18);
      transform: translateY(-50%) scale(1.05);
    }

    ${arrowMode === "outside"
      ? `
          .bc-left  { left:  -60px; }
          .bc-right { right: -60px; }
          @media (max-width: 1024px) {
            .bc-left  { left:  -20px; }
            .bc-right { right: -20px; }
          }
        `
      : `
          .bc-left  { left:  -5px; }
          .bc-right { right: -5px; }
        `
    }

    /* dots */
    .bc-dotsRow {
      display: flex;
      justify-content: center;
      gap: 8px;
      padding: 10px 0 2px;
    }
    .bc-dot {
      width: 10px;
      height: 10px;
      border-radius: 999px;
      border: none;
      cursor: pointer;
      background: rgba(15,23,42,.18);
      transition: background 250ms ease, transform 250ms ease;
      padding: 0;
    }
    .bc-dot.active {
      background: rgba(15,23,42,.80);
      transform: scale(1.25);
    }

    /* skeleton shimmer */
    .bc-skeleton {
      height: clamp(220px, 26vw, ${hD}px);
      background: linear-gradient(
        90deg,
        rgba(0,0,0,.04) 0%,
        rgba(0,0,0,.08) 50%,
        rgba(0,0,0,.04) 100%
      );
      background-size: 240% 100%;
      animation: bcShimmer 1.4s infinite;
    }
    @media (max-width: 1024px) {
      .bc-skeleton { height: ${hT}px; }
    }
    @media (max-width: 640px) {
      .bc-skeleton { height: ${hM}px; }
    }
    @keyframes bcShimmer {
      0%   { background-position: 0% 0; }
      100% { background-position: 140% 0; }
    }
  `;
}